import { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Globe, Instagram, MapPin, MessageCircle } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import BrandLogo from '../components/BrandLogo';
import { Button } from '../components/ui/button';
import { normalizeObjectStrings } from '../lib/utils';
import { SUPPLIERS_BETA_ENABLED } from '../lib/featureFlags';
import {
  getSupplierLocationLabel,
  getSupplierPlanMeta,
  normalizeInstagramLink,
  normalizeWhatsappLink,
  SUPPLIER_COVER_FALLBACK,
  SUPPLIER_PLAN_OPTIONS,
} from '../lib/suppliers';
import { getSupplierPreviewBySlug, getSupplierPreviewWithFallback } from '../lib/supplierPreviewContent';

const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const API = `${BACKEND_URL}/api`;

export default function SupplierProfilePage() {
  const { slug } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!SUPPLIERS_BETA_ENABLED || !slug) return undefined;

    let active = true;
    setLoading(true);

    axios.get(`${API}/suppliers/public/${encodeURIComponent(slug)}`)
      .then((response) => {
        if (active) setSupplier(normalizeObjectStrings(response.data));
      })
      .catch(() => {
        if (active) setSupplier(getSupplierPreviewWithFallback(getSupplierPreviewBySlug(slug)));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [slug]);

  if (!SUPPLIERS_BETA_ENABLED) {
    return <Navigate to="/" replace />;
  }

  const planMeta = getSupplierPlanMeta(supplier?.plan_type);
  const activePlan = SUPPLIER_PLAN_OPTIONS.find((plan) => plan.id === String(supplier?.plan_type || 'free').trim().toLowerCase()) || SUPPLIER_PLAN_OPTIONS[0];
  const whatsappLink = normalizeWhatsappLink(supplier?.whatsapp);
  const instagramLink = normalizeInstagramLink(supplier?.instagram);
  const websiteLink = String(supplier?.website_url || '').trim() || null;

  return (
    <div className="min-h-screen bg-[#F6F1EF] text-[#1C1C1C]">
      <SEOHead
        title={supplier?.public_name ? `${supplier.public_name} | Fornecedores` : 'Fornecedor | Virei Noiva'}
        description={supplier?.short_description || 'Perfil de fornecedor no diretorio do Virei Noiva.'}
        noindex
      />

      <header className="border-b border-[#E8B7B1]/35 bg-white/90 backdrop-blur">
        <div className="container-wedding flex h-20 items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center">
            <BrandLogo size="lg" showTagline />
          </Link>
          <Link to="/fornecedores">
            <Button variant="outline" className="rounded-full border-[#E8B7B1] bg-white text-[#9A6B65] hover:bg-[#F6F1EF]">
              Ver diretorio
            </Button>
          </Link>
        </div>
      </header>

      <main className="container-wedding py-12">
        {loading ? (
          <div className="rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-10 text-center text-[#5A4C49]">
            Carregando perfil...
          </div>
        ) : !supplier ? (
          <div className="rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-10 text-center text-[#5A4C49]">
            Esse fornecedor nao foi encontrado.
          </div>
        ) : (
          <div className="space-y-8">
            <section className="overflow-hidden rounded-[2rem] border border-[#E8B7B1]/35 bg-white">
              <div className="aspect-[16/7] overflow-hidden">
                <img
                  src={supplier.cover_image || SUPPLIER_COVER_FALLBACK}
                  alt={supplier.public_name}
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="grid gap-6 p-8 lg:grid-cols-[1fr_320px]">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${planMeta.badgeClass}`}>
                      {planMeta.label}
                    </span>
                    {supplier.is_verified ? (
                      <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                        Verificado
                      </span>
                    ) : null}
                    {supplier.is_homepage_featured ? (
                      <span className="inline-flex rounded-full bg-[#1C1C1C] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white">
                        Destaque
                      </span>
                    ) : null}
                  </div>

                  <h1 className="mt-4 text-4xl md:text-5xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {supplier.public_name}
                  </h1>
                  <p className="mt-3 text-lg text-[#9A6B65]">{supplier.category || 'Fornecedor de casamento'}</p>
                  <p className="mt-3 flex items-center gap-2 text-[#5A4C49]">
                    <MapPin className="h-4 w-4 text-[#C8A97E]" />
                    {getSupplierLocationLabel(supplier)}
                  </p>

                  {supplier.short_description ? (
                    <p className="mt-5 max-w-3xl text-base leading-relaxed text-[#333333]">{supplier.short_description}</p>
                  ) : null}

                  {supplier.full_description ? (
                    <div className="mt-6 whitespace-pre-line text-sm leading-relaxed text-[#333333]">
                      {supplier.full_description}
                    </div>
                  ) : null}
                </div>

                <aside className={`rounded-[1.6rem] border p-5 ${planMeta.cardClass}`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">Contato</p>
                  <div className="mt-4 grid gap-3">
                    {whatsappLink ? (
                      <a href={whatsappLink} target="_blank" rel="noreferrer">
                        <Button className={`${supplier?.plan_type === 'premium_plus' ? 'bg-[#1C1C1C] hover:bg-[#333333]' : 'bg-[#E8B7B1] hover:bg-[#DDA39D]'} h-11 w-full rounded-full text-white`}>
                          <MessageCircle className="mr-2 h-4 w-4" />
                          Falar no WhatsApp
                        </Button>
                      </a>
                    ) : null}

                    {instagramLink ? (
                      <a href={instagramLink} target="_blank" rel="noreferrer">
                        <Button variant="outline" className="h-11 w-full rounded-full border-[#E8B7B1] bg-white text-[#9A6B65] hover:bg-[#F6F1EF]">
                          <Instagram className="mr-2 h-4 w-4" />
                          Ver Instagram
                        </Button>
                      </a>
                    ) : null}

                    {websiteLink ? (
                      <a href={websiteLink} target="_blank" rel="noreferrer">
                        <Button variant="outline" className="h-11 w-full rounded-full border-[#E8B7B1] bg-white text-[#9A6B65] hover:bg-[#F6F1EF]">
                          <Globe className="mr-2 h-4 w-4" />
                          Acessar site
                        </Button>
                      </a>
                    ) : null}
                  </div>

                  {!whatsappLink && !instagramLink && !websiteLink ? (
                    <p className="mt-4 text-sm leading-relaxed text-[#5A4C49]">
                      Este fornecedor ainda nao configurou um canal publico de contato.
                    </p>
                  ) : null}

                  <Link to="/fornecedores" className="mt-5 inline-flex items-center text-sm font-medium text-[#B97D5B]">
                    Voltar ao diretorio
                    <ExternalLink className="ml-1.5 h-4 w-4" />
                  </Link>
                </aside>
              </div>
            </section>

            <section className="grid gap-4 md:grid-cols-3">
              <article className="rounded-[1.5rem] border border-[#E8B7B1]/35 bg-white p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">Plano visivel</p>
                <p className="mt-3 text-xl text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {planMeta.label}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#4B403D]">{activePlan.description}</p>
              </article>

              <article className="rounded-[1.5rem] border border-[#E8B7B1]/35 bg-white p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">Distribuicao</p>
                <p className="mt-3 text-sm leading-relaxed text-[#4B403D]">
                  {supplier?.is_homepage_featured
                    ? 'Este perfil participa das vitrines de destaque da home e reforca percepcao premium.'
                    : 'Este perfil esta preparado para descoberta dentro do diretorio do Virei Noiva.'}
                </p>
              </article>

              <article className="rounded-[1.5rem] border border-[#E8B7B1]/35 bg-white p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">Contato direto</p>
                <p className="mt-3 text-sm leading-relaxed text-[#4B403D]">
                  O casal pode sair deste perfil direto para WhatsApp, Instagram ou site, reduzindo atrito comercial.
                </p>
              </article>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
